import React, { useState, useEffect } from "react";
import api, { apiError, setCsrf } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { KeyRound, Loader2 } from "lucide-react";

export default function AccountSettingsDialog({ open, onOpenChange, session }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const isFounder = !!session?.platform_role;
  const [rcRemaining, setRcRemaining] = useState(null);
  const [rcCodes, setRcCodes] = useState(null);
  const [rcBusy, setRcBusy] = useState(false);

  useEffect(() => {
    if (!open) { setRcCodes(null); return; }
    if (isFounder) api.get("/auth/recovery-codes").then((r) => setRcRemaining(r.data.remaining)).catch(() => {});
  }, [open, isFounder]);

  const genCodes = async () => {
    setRcBusy(true);
    try {
      const { data } = await api.post("/auth/recovery-codes/generate");
      setRcCodes(data.codes);
      setRcRemaining(data.codes.length);
      toast.success("Recovery codes generated");
    } catch (e) { toast.error(apiError(e)); } finally { setRcBusy(false); }
  };

  const roleLabel = session?.platform_role
    ? "Founder"
    : (session?.roles?.[0] || "").replace(/_/g, " ") || "Member";

  const reset = () => { setCur(""); setNw(""); setConfirm(""); };

  const save = async () => {
    if (!cur || !nw) return toast.error("Enter your current and new password");
    if (nw.length < 8) return toast.error("New password must be at least 8 characters");
    if (nw !== confirm) return toast.error("New passwords do not match");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      if (data?.csrf) setCsrf(data.csrf);  // password change rotates the session; refresh CSRF token
      toast.success("Password updated");
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle>Account settings</DialogTitle>
          <DialogDescription>Manage your account and password.</DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-4 text-sm">
          <div className="flex justify-between py-1"><span className="text-slate-500">Name</span><span className="font-medium" data-testid="settings-name">{session?.user?.name || "—"}</span></div>
          <div className="flex justify-between py-1"><span className="text-slate-500">Email</span><span className="font-medium" data-testid="settings-email">{session?.user?.email}</span></div>
          <div className="flex justify-between py-1"><span className="text-slate-500">Role</span><span className="font-medium capitalize" data-testid="settings-role">{roleLabel}</span></div>
        </div>

        <div className="mt-2 space-y-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Change password</div>
          <div>
            <Label htmlFor="cur-pw">Current password</Label>
            <Input id="cur-pw" type="password" data-testid="settings-current-pw" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1.5" autoComplete="current-password" />
          </div>
          <div>
            <Label htmlFor="new-pw">New password</Label>
            <Input id="new-pw" type="password" data-testid="settings-new-pw" value={nw} onChange={(e) => setNw(e.target.value)} className="mt-1.5" autoComplete="new-password" placeholder="At least 8 characters" />
          </div>
          <div>
            <Label htmlFor="confirm-pw">Confirm new password</Label>
            <Input id="confirm-pw" type="password" data-testid="settings-confirm-pw" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" autoComplete="new-password" onKeyDown={(e) => { if (e.key === "Enter") save(); }} />
          </div>
          <Button className="w-full" data-testid="settings-save" disabled={busy} onClick={save}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="mr-1.5 h-4 w-4" /> Update password</>}
          </Button>
        </div>

        {isFounder && (
          <div className="mt-2 space-y-3 border-t border-slate-200 dark:border-slate-800 pt-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Backup recovery codes</div>
            <p className="text-sm text-slate-500">
              Sign in if email 2FA is ever unavailable — enter a code at the verification step. Each code works once.
              {rcRemaining != null && ` ${rcRemaining} unused remaining.`}
            </p>
            {rcCodes && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3">
                <p className="mb-2 text-xs font-semibold text-amber-700 dark:text-amber-400">Save these now — they will not be shown again.</p>
                <div className="grid grid-cols-2 gap-1 font-mono text-sm" data-testid="recovery-codes-list">
                  {rcCodes.map((c) => <div key={c}>{c}</div>)}
                </div>
              </div>
            )}
            <Button variant="outline" className="w-full" data-testid="generate-recovery-codes" disabled={rcBusy} onClick={genCodes}>
              {rcBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : (rcRemaining ? "Regenerate recovery codes" : "Generate recovery codes")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
